import {
  Autocomplete,
  AutocompleteOwnerState,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderGroupParams,
  TextField,
} from '@mui/material';
import IncFormHelperText from 'components/forms/inc-form-helper-text/inc-form-helper-text.component';
import React, { ReactNode, useCallback } from 'react';
import { FieldError } from 'react-hook-form';
import AutocompleteOption from '../autocomplete-option/autocomplete-option.component';
import { matchWithOption } from './autocomplete-base.utils';

export type AutocompleteBaseProps = {
  freeSolo?: boolean;
  disableCloseOnSelect?: boolean;
  value?: any;
  inputValue?: string;
  helperText?: ReactNode;
  popupIcon?: any;
  error?: FieldError;
  defaultValue?: any;
  label?: ReactNode;
  options: any;
  filterOptions?: any;
  noOptionsText?: string;
  ListboxProps?: any;
  OptionProps?: any;
  InputProps?: any;
  TextFieldProps?: any;
  sx?: object;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  onInputChange?: (_e: any, newValue: any, reason: string) => void;
  getOptionLabel?: (option: any) => string;
  renderOption?: (props: any, option: any, state?: any) => React.ReactElement;
  onChange?: (_e: any, option: any, reason: any) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  multiple?: boolean;
  ChipProps?: any;
  disableClearable?: boolean;
  disabled?: boolean;
  limitTags?: number;
  size?: 'small' | 'medium';
  inputTextField?: any;
  disableListWrap?: any;
  PopperComponent?: any;
  ListboxComponent?: any;
  fullWidth?: boolean;
  loading?: boolean;
  renderInput?: (params: any) => React.ReactNode;
  select?: boolean;
  open?: boolean;
  onOpen?: (event: React.SyntheticEvent<Element, Event>) => void;
  onClose?: (event: React.SyntheticEvent<Element, Event>) => void;
  onFocus?: (event: React.SyntheticEvent<Element, Event>) => void;
  readOnly?: boolean;
  inputProps?: any;
  groupBy?: (option) => string;
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
  renderTags?: (
    value: any[],
    getTagProps: AutocompleteRenderGetTagProps,
    ownerState: AutocompleteOwnerState<any, boolean, boolean, boolean, 'div'>,
  ) => React.ReactNode;
  getOptionDisabled?: (option: any) => boolean;
  clearOnEscape?: boolean;
  clearIcon?: React.ReactNode;
  disablePortal?: boolean;
};

const AutocompleteBase = React.forwardRef(
  (
    {
      helperText,
      error,
      label,
      options,
      ListboxProps = {},
      OptionProps = {},
      InputProps = {},
      inputProps = {},
      TextFieldProps = {},
      isOptionEqualToValue = (option, value) => option.id === value,
      multiple,
      inputTextField,
      sx,
      onBlur,
      disablePortal = true,
      ...rest
    }: AutocompleteBaseProps,
    ref,
  ) => {
    const getOptionLabel = useCallback(
      option =>
        option?.label ??
        options?.find(op => op.id === matchWithOption(option))?.label ??
        '',
      [options],
    );

    return (
      <Autocomplete
        disablePortal={disablePortal}
        fullWidth
        options={options}
        isOptionEqualToValue={isOptionEqualToValue}
        renderInput={params => (
          <>
            <TextField
              {...params}
              {...inputTextField}
              label={label}
              error={!!error}
              helperText={
                <IncFormHelperText error={error} helperText={helperText} />
              }
              InputProps={{
                ...params.InputProps,
                ...InputProps,
              }}
              inputProps={{
                autoComplete: 'off', // disable autocomplete and autofill
                ...params.inputProps,
                ...inputProps,
              }}
              {...TextFieldProps}
              inputRef={ref}
            />
          </>
        )}
        renderOption={(props, option, { selected }) => (
          <AutocompleteOption
            multiple={multiple}
            props={props}
            option={option}
            selected={selected}
            OptionProps={OptionProps}
            {...props}
          />
        )}
        ListboxProps={{
          'data-testid': 'Autocomplete.Listbox',
          ...ListboxProps,
        }}
        sx={{
          '&& .MuiChip-sizeSmall': {
            maxHeight: '21px',
          },
          ...sx,
        }}
        getOptionLabel={getOptionLabel}
        multiple={multiple}
        disableCloseOnSelect={multiple}
        onBlur={onBlur}
        {...rest}
      />
    );
  },
);

export default AutocompleteBase;
